import React, { useState, useEffect, useCallback } from 'react';
import { BASE_URL } from '../constants/config';
import { fetchWithAuth } from '../constants/api';
import { showToast, showAlert } from '../components/Toast';
import AppShell from '../components/AppShell';
import Modal from '../components/Modal';
import { formatDate, formatINR, TXN_STYLES, PAYMENT_CATEGORY_STYLES } from '../utils/expenseFormat';

const ExpenseApprovalScreen = ({ navigate, onBack }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchPending = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await fetchWithAuth(`${BASE_URL}api/Expense/PendingApprovals`);
      if (response.ok) setExpenses(await response.json());
      else showToast(`Failed to load pending approvals (${response.status})`, 'error');
    } catch (_) {
      showToast('Could not load pending approvals. Please check your connection.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = (item) => {
    showAlert('Approve Expense', `Approve this ${formatINR(item.Amount)} expense from ${item.InsertedByName || 'this employee'}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Approve', style: 'destructive', onPress: async () => {
          try {
            const response = await fetchWithAuth(`${BASE_URL}api/Expense/Approve/${item.ID}`, { method: 'PUT' });
            if (response.ok) {
              showToast('Expense approved.', 'success');
              fetchPending();
            } else {
              showToast(`Failed to approve expense (${response.status})`, 'error');
            }
          } catch (_) {
            showToast('Could not approve expense.', 'error');
          }
        }
      },
    ]);
  };

  const openReject = (item) => {
    setRejectTarget(item);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) {
      showToast('Please enter a reason for rejection.', 'error');
      return;
    }
    try {
      setRejecting(true);
      const response = await fetchWithAuth(`${BASE_URL}api/Expense/Reject/${rejectTarget.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ RejectionReason: rejectReason.trim() }),
      });
      if (response.ok) {
        showToast('Expense rejected.', 'success');
        setRejectTarget(null);
        fetchPending();
      } else {
        showToast(`Failed to reject expense (${response.status})`, 'error');
      }
    } catch (_) {
      showToast('Could not reject expense.', 'error');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Pending Approvals" onBack={onBack}>
        <div className="loading-screen">
          <div className="spinner spinner--gold" />
          <p>Loading pending approvals...</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Pending Approvals"
      onBack={onBack}
      rightAction={{ icon: '↻', onClick: () => fetchPending(true), label: 'Refresh' }}
    >
      {refreshing && (
        <div style={{ textAlign: 'center', padding: '12px', color: '#999', fontSize: 13 }}>
          Refreshing...
        </div>
      )}

      {expenses.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: 60, color: '#aaa', fontSize: 15 }}>
          No expenses awaiting approval.
        </div>
      ) : (
        <div className="expense-list" style={{ paddingBottom: 40 }}>
          {expenses.map(item => (
            <div key={item.ID} className="approval-item">
              <div
                className="approval-item__row"
                onClick={() => navigate?.('ExpenseDetail', { expense: item, fromScreen: 'ExpenseApproval' })}
              >
                <div className="approval-item__content">
                  <div className="expense-party">{item.ExpPartyName || 'No Party'}</div>
                  <div className="expense-cat">
                    {item.ExpCatName || 'Other'}
                    {item.ExpSubCatName ? ` › ${item.ExpSubCatName}` : ''}
                  </div>
                  {item.Detail && (
                    <div className="expense-detail-text">📝 {item.Detail}</div>
                  )}
                  <div className="expense-inserted-on">
                    👤 {item.InsertedByName || 'Unknown'} &nbsp;·&nbsp; 📅 {formatDate(item.Date)}
                    {TXN_STYLES[item.TransactionType] && (
                      <span style={{ color: TXN_STYLES[item.TransactionType].color, fontWeight: 600 }}>
                        &nbsp;·&nbsp;{TXN_STYLES[item.TransactionType].icon} {TXN_STYLES[item.TransactionType].label}
                      </span>
                    )}
                    {PAYMENT_CATEGORY_STYLES[item.PaymentCategory] && (
                      <span style={{ color: PAYMENT_CATEGORY_STYLES[item.PaymentCategory].color, fontWeight: 600 }}>
                        &nbsp;·&nbsp;{PAYMENT_CATEGORY_STYLES[item.PaymentCategory].icon} {PAYMENT_CATEGORY_STYLES[item.PaymentCategory].label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="expense-amount" style={{ color: '#c62828' }}>
                  −{formatINR(item.Amount || 0).replace('₹ ', '')}
                </div>
              </div>
              <div className="approval-item__actions">
                <button className="btn-primary btn-outline btn-sm" style={{ flex: 1, height: 36 }} onClick={() => openReject(item)}>
                  Reject
                </button>
                <button className="btn-primary btn-green btn-sm" style={{ flex: 1, height: 36 }} onClick={() => handleApprove(item)}>
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        visible={!!rejectTarget}
        onDismiss={() => setRejectTarget(null)}
        title="Reject Expense"
        footer={(
          <button className="btn-primary btn-danger-outline" disabled={rejecting} onClick={submitReject}>
            {rejecting ? 'Rejecting...' : 'Confirm Reject'}
          </button>
        )}
      >
        <div className="form-group" style={{ padding: 16 }}>
          <label className="form-label">Reason for rejection</label>
          <textarea
            className="form-input"
            rows={3}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain why this expense is being rejected..."
          />
        </div>
      </Modal>

      <style>{`
        .approval-item {
          background: #fff;
          border-bottom: 0.5px solid #f0f0f0;
          padding: 10px 16px;
        }
        .approval-item__row {
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        .approval-item__content { flex: 1; min-width: 0; }
        .approval-item__actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }
        .approval-item .expense-party {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .approval-item .expense-cat {
          font-size: 12px;
          color: #999;
          margin-top: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .approval-item .expense-detail-text {
          font-size: 12px;
          color: #777;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .approval-item .expense-inserted-on {
          font-size: 10px;
          color: #b0b0b0;
          margin-top: 2px;
        }
        .approval-item .expense-amount {
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
          margin-left: 8px;
        }
      `}</style>
    </AppShell>
  );
};

export default ExpenseApprovalScreen;
